import { useState, useRef, useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import JobTitleTypeahead from './JobTitleTypeahead';
import ManagementLevelChecklist, {
  toSurfeSeniorities,
  levelForSeniority
} from './ManagementLevelChecklist';
import KeyContactsMobileSheet from './KeyContactsMobileSheet';
import { request } from '../../../../api/api';
import { API_ENDPOINTS } from '../../../../api/endpoints';
import { mapDiscoverResponse } from '../../../../api/dataMappers';

/**
 * DiscoverContactsPanel (TD-2105, Contact Enrichment Stream B)
 *
 * "Discover contacts" trigger + dropdown panel for the Key Contacts action bar.
 * Composes a job-title typeahead (AND) a management-level checklist, then runs
 * a free discovery search (no credit cost — only reveal costs).
 *
 * Results are handed to the parent via `onResults(contacts)`; the parent
 * (KeyContactsTab, TD-2106) owns the ephemeral rendering. Mount gating
 * (STREAM_B_ENABLED + access/feature flags) is the parent's responsibility so
 * this component stays a pure, testable unit.
 *
 * The popover is an absolute dropdown anchored to the trigger wrapper (TD-2420),
 * so it scrolls with the page like the Reveal-selected dropdown instead of
 * floating on scroll; outside-click detection uses a document listener that
 * ignores clicks inside the wrapper (which now contains the popover).
 */

// TD-2485 (AC6): mobile Search-footer label with active-filter count. Kept as a
// flat if/else (not a nested ternary) for readability + Sonar.
const buildSearchLabel = (loading, filterCount) => {
  if (loading) return 'Searching…';
  if (!filterCount) return 'Search';
  return `Search · ${filterCount} filter${filterCount === 1 ? '' : 's'}`;
};

const DiscoverContactsPanel = ({ companyId, onResults, onCriteriaChange, layout = 'desktop' }) => {
  const [open, setOpen] = useState(false);
  const [jobTitles, setJobTitles] = useState([]);
  const [managementLevels, setManagementLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const wrapRef = useRef(null);
  const popoverRef = useRef(null);
  const reactId = useId();
  const titleId = `${reactId}-title`;
  const jobTitlesInputId = `${reactId}-job-titles`;

  const hasCriteria = jobTitles.length > 0 || managementLevels.length > 0;
  // TD-2485: on mobile the panel renders as a modal bottom sheet
  // (KeyContactsMobileSheet); on desktop it stays the anchored popover.
  const isMobile = layout === 'mobile';

  // TD-2497: the management-level selection doubles as the discovered-list display
  // filter (owned by KeyContactsTab). Route every change through here so a toggle
  // reaches the parent synchronously — the parent re-filters the retained results,
  // so deselect/re-select never re-hits the discovery API (no cost). Kept as a
  // plain setter (no effect) to stay in sync.
  //
  // The ref mirrors the same value because `handleSearch` closes over
  // `managementLevels` at the render it was created in: a level ticked while a
  // request is in flight would otherwise be dropped from the union the response
  // reports up. The ref is always current, so the union is too.
  const managementLevelsRef = useRef(managementLevels);

  const applyLevels = (next) => {
    managementLevelsRef.current = next;
    setManagementLevels(next);
    onCriteriaChange?.(next);
  };

  const closePanel = () => {
    setOpen(false);
    // Return focus to the trigger wrapper (tabIndex=-1) for keyboard users.
    wrapRef.current?.focus?.();
  };

  // Close on outside click (the popover is a child of the trigger wrapper) and on
  // Escape. Desktop only — on mobile the bottom sheet's useModalA11y owns Escape
  // and backdrop light-dismiss, and the sheet is portaled to the top layer (a
  // click inside it would read as "outside" this wrapper and close prematurely).
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onDocPointer = (e) => {
      const inTrigger = wrapRef.current?.contains(e.target);
      const inPopover = popoverRef.current?.contains(e.target);
      if (!inTrigger && !inPopover) closePanel();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, isMobile]);

  const handleSearch = async () => {
    if (!hasCriteria || loading) return;
    if (!companyId) {
      setError('Company information is still loading. Please try again in a moment.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // The BE expects a Surfe /v2/people/search body under `criteria` and
      // scopes the search to the company server-side from `profileId` (so we do
      // NOT send company name/domain). Job titles + management levels combine
      // with AND logic. Only include the sub-filters that are actually set.
      const seniorities = toSurfeSeniorities(managementLevels);
      const people = {
        ...(jobTitles.length ? { jobTitles } : {}),
        ...(seniorities.length ? { seniorities } : {})
      };
      const res = await request({
        endpoint: API_ENDPOINTS.DISCOVER_CONTACTS,
        body: { profileId: companyId, criteria: { people } }
      });
      // `request` resolves a live BE response to the FLAT body the endpoint
      // returns ({ contacts, totalBeforeDedupe, totalAfterDedupe }) spread with an
      // `httpStatus` — there is NO { success, data } envelope here (that shape
      // only ever came from the now-removed mock path, and the endpoint's
      // dataMapper never runs on a real response). Timeouts/aborts resolve to
      // { success: false }. So detect failure from httpStatus, not `success`,
      // and map the flat body ourselves (mirrors the reveal endpoints).
      const failed = !res || res.success === false || (res.httpStatus ?? 200) >= 400;
      if (failed) {
        setError('Something went wrong while discovering contacts. Please try again.');
      } else {
        // Read `contacts` from the flat body; tolerate a legacy `{ data }` wrapper
        // too so this holds regardless of what the live capture confirms (R2).
        const payload = res.data ?? res;
        const contacts = mapDiscoverResponse(payload).contacts;
        // TD-2497: report the management levels the returned people came back as,
        // so the parent's display filter shows them — including a title-only
        // search whose seniority levels the user never ticked. Free-text
        // seniorities map to no level and are shown regardless.
        //
        // This is reported UP ONLY and never written back into `managementLevels`:
        // the checklist reflects only the user's OWN criteria (what the next
        // search asks for), while the parent's display filter is the union of
        // those plus the levels actually returned. Auto-checking here would
        // silently add `seniorities` to the NEXT request's criteria and narrow a
        // search the user never narrowed.
        const foundLevels = [
          ...new Set(contacts.map((c) => levelForSeniority(c.manager_function)).filter(Boolean))
        ];
        if (foundLevels.length) {
          onCriteriaChange?.([...new Set([...managementLevelsRef.current, ...foundLevels])]);
        }
        onResults(contacts);
        setOpen(false);
      }
    } catch {
      setError('Something went wrong while discovering contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Shared across the desktop popover and the mobile sheet so the two presentations
  // never drift.
  const trigger = (
    <IconButton
      wrapClassName="key-contacts-discover-trigger"
      type={ICON_BUTTON_TYPES.BLUE}
      icon={ICON_BUTTON_ICONS.SEARCH}
      innerText="Discover contacts"
      filled={open}
      onClick={() => setOpen((prev) => !prev)}
    />
  );

  const jobTitlesField = (
    <>
      <label className="discover-contacts-field-label" htmlFor={jobTitlesInputId}>
        Job titles
      </label>
      <JobTitleTypeahead
        value={jobTitles}
        onChange={setJobTitles}
        inputId={jobTitlesInputId}
        // TD-2528: on mobile a bare tap must not auto-open the list (it would
        // cover the Management level pills below); it opens once the user types.
        openOnFocus={!isMobile}
      />
    </>
  );

  const statusAndError = (
    <>
      <div className="discover-contacts-status" role="status" aria-live="polite">
        {loading ? 'Searching for contacts…' : ''}
      </div>
      {!!error && (
        <div className="discover-contacts-error" role="alert">
          {error}
        </div>
      )}
    </>
  );

  // TD-2485 (AC6): the mobile Search footer surfaces the active-filter count.
  const activeFilterCount = jobTitles.length + managementLevels.length;
  const searchLabel = buildSearchLabel(loading, activeFilterCount);

  if (isMobile) {
    return (
      <div className="discover-contacts-panel" ref={wrapRef} tabIndex={-1}>
        {trigger}
        <KeyContactsMobileSheet
          isOpen={open}
          onClose={closePanel}
          title="Discover contacts"
          footer={
            <IconButton
              type={ICON_BUTTON_TYPES.BLUE}
              innerText={searchLabel}
              filled
              wrapClassName="discover-contacts-sheet-search"
              disabled={!hasCriteria || loading}
              onClick={handleSearch}
            />
          }
        >
          {jobTitlesField}
          {/* Pills (not a single control), so a non-associated <label> would trip
              jsx-a11y — use a plain field label; the pill group carries its own
              aria-label. */}
          <div className="discover-contacts-field-label">Management level</div>
          <ManagementLevelChecklist
            value={managementLevels}
            onChange={applyLevels}
            variant="pills"
          />
          {statusAndError}
        </KeyContactsMobileSheet>
      </div>
    );
  }

  return (
    <div className="discover-contacts-panel" ref={wrapRef} tabIndex={-1}>
      {trigger}

      {open && (
        <div
          className="discover-contacts-popover"
          ref={popoverRef}
          role="dialog"
          aria-labelledby={titleId}
        >
          <div className="discover-contacts-popover-header">
            <div className="title" id={titleId}>
              Discover contacts
            </div>
            <IconButton
              type={ICON_BUTTON_TYPES.BLUE}
              icon={ICON_BUTTON_ICONS.X_LG}
              withFill={false}
              withBorder={false}
              tooltipText="Close"
              onClick={closePanel}
            />
          </div>

          <div className="discover-contacts-popover-body">
            {jobTitlesField}

            <ManagementLevelChecklist value={managementLevels} onChange={applyLevels} />

            {statusAndError}
          </div>

          <div className="discover-contacts-popover-actions">
            <IconButton
              type={ICON_BUTTON_TYPES.BLUE}
              innerText={loading ? 'Searching…' : 'Search'}
              filled
              disabled={!hasCriteria || loading}
              onClick={handleSearch}
            />
          </div>
        </div>
      )}
    </div>
  );
};

DiscoverContactsPanel.propTypes = {
  companyId: PropTypes.string.isRequired,
  onResults: PropTypes.func.isRequired,
  // TD-2497: reports the display filter up so the parent can filter the
  // discovered list by it — the user's own level selection on a toggle, and its
  // union with the levels actually returned after a search.
  onCriteriaChange: PropTypes.func,
  layout: PropTypes.oneOf(['desktop', 'mobile', 'tablet'])
};

export default DiscoverContactsPanel;
